// =====================================================================
// src/lib/v2/firestore/businesses.js
//
// V2 Firestore READ layer for businesses and their products subcollection.
//
// This file is pure data fetching — no UI logic, no derivations, no caching
// beyond what Firestore already provides. Screens import these functions
// instead of querying Firestore directly, so the query shape lives in one
// place.
//
// Conventions:
//   - All functions return Promises (use with async/await)
//   - All functions throw on error — let screen-level code catch & display
//   - Returned objects have shape { id, ...firestoreData }
//   - Doc IDs are slugs ('sharma-sweets'), NOT numeric ('b1')
//
// Used by:
//   - BusinessDirectory.jsx (Step 3.2)
//   - BusinessProfile.jsx   (Step 3.3)
//   - MerchantApp.jsx       (Step 3.6)
// =====================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';

// ============================================================
// listBusinesses(options)
// ============================================================
//
// Reads all customer-visible businesses from the /businesses collection.
//
// By default returns status IN ['verified', 'pending_verification'] so newly
// registered businesses show on the directory immediately (with no ✓ badge
// until admin verifies). Suspended businesses are always hidden.
//
// Options:
//   includeUnverified (bool, default false) — include 'pending_verification'.
//                                              Already default true behaviour;
//                                              kept for backward compatibility.
//   ownerId (string, optional) — filter to only businesses owned by this UID.
//                                When set, status filter is dropped (owner
//                                sees all their own businesses regardless).
//
// Returns: Array<Business> ordered alphabetically by name.
// Throws: any Firestore error.
//
export async function listBusinesses(options = {}) {
  const { ownerId = null } = options;

  const constraints = [];

  if (ownerId) {
    // Owner querying own businesses — no status filter, they see all.
    constraints.push(where('ownerId', '==', ownerId));
  } else {
    // Customer directory query — verified + pending_verification.
    // Aligns with the rule's read branch which allows both.
    constraints.push(where('status', 'in', ['verified', 'pending_verification']));
  }

  // Sort alphabetically by name for stable directory order
  constraints.push(orderBy('name'));

  const q = query(collection(db, 'businesses'), ...constraints);

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ============================================================
// getBusiness(businessId)
// ============================================================
//
// Reads a single business document by ID (slug like 'sharma-sweets').
//
// Returns: Business object { id, name, ... } or null if not found.
// Throws: Firestore errors (permission, network).
//
export async function getBusiness(businessId) {
  if (!businessId) {
    throw new Error('getBusiness: businessId is required');
  }

  const ref = doc(db, 'businesses', businessId);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return null;
  }

  return { id: snap.id, ...snap.data() };
}

// ============================================================
// getProducts(businessId)
// ============================================================
//
// Reads all products in the /businesses/{businessId}/products subcollection.
//
// By default returns products ordered by name. Includes both in-stock and
// out-of-stock (UI decides how to display). Filtering by inStock happens
// client-side since it's a small list per business.
//
// Returns: Array<Product>
//   where Product = { id, name, price, unit, inStock, ... }
//
// Throws: Firestore errors.
//
export async function getProducts(businessId) {
  if (!businessId) {
    throw new Error('getProducts: businessId is required');
  }

  const ref = collection(db, 'businesses', businessId, 'products');
  const q = query(ref, orderBy('name'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ============================================================
// getBusinessesByOwners(userIds, options)
// ============================================================
//
// Given an array of user UIDs, returns a Map<ownerUid, Business[]> of
// businesses owned by each.
//
// Default: returns only VERIFIED businesses — used by MembersTabV2 to
// render the "Owns Sharma Sweets" pill next to batch members. We don't
// want to show that pill for businesses that haven't been admin-approved.
//
// With `includeUnverified: true`: returns all statuses (verified +
// pending_verification + anything else owned). Used by:
//   - MerchantApp (owner needs to manage their pending biz immediately
//     after registration, before admin verifies)
//   - HomeStripV2 owner-check (flip "+ Register" → "My Business" the
//     moment someone has any business, pending or not)
//   - ProfileTabV2 owner-card (same)
//
// Mechanics:
//   - Firestore 'in' clause accepts max 30 values per query, so we chunk
//   - Each chunk runs in parallel
//   - When filtering by status, we use a separate where() — rule check
//     aligns: non-owner reads need status='verified', owner reads any.
//   - Caller-supplied userIds are deduped defensively
//
// Returns: Map<string, Business[]>
//   - Map only contains keys for UIDs that own at least one business
//   - Each Business has the full /businesses/{id} doc shape
//
// Requires composite index: businesses (ownerId ASC, status ASC).
// Both branches of the includeUnverified flag use the status field
// (either '==' or 'in'), so the same composite index serves both.
//
export async function getBusinessesByOwners(userIds, options = {}) {
  const { includeUnverified = false } = options;
  const result = new Map();
  if (!Array.isArray(userIds) || userIds.length === 0) return result;

  // Dedupe + filter falsy
  const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
  if (uniqueIds.length === 0) return result;

  // Chunk into groups of 30 (Firestore 'in' limit)
  const CHUNK_SIZE = 30;
  const chunks = [];
  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
  }

  await Promise.all(chunks.map(async (chunk) => {
    const constraints = [where('ownerId', 'in', chunk)];
    if (!includeUnverified) {
      // Default: verified-only. Matches the rule's general read branch.
      constraints.push(where('status', '==', 'verified'));
    } else {
      // includeUnverified: rule still blocks 'suspended', so we MUST
      // pre-constrain status to rule-allowed values — dropping the
      // filter entirely would make Firestore reject the query upfront
      // with permission-denied (because the query shape could match
      // suspended docs). Mirrors listBusinesses().
      constraints.push(where('status', 'in', ['verified', 'pending_verification']));
    }
    const q = query(collection(db, 'businesses'), ...constraints);
    const snap = await getDocs(q);
    snap.docs.forEach(d => {
      const biz = { id: d.id, ...d.data() };
      const owner = biz.ownerId;
      if (!owner) return;
      // Client-side guard: exclude suspended biz even with includeUnverified,
      // since a suspended biz shouldn't be manageable. (Defensive — the
      // status filter above should already exclude these.)
      if (includeUnverified && biz.status === 'suspended') return;
      if (!result.has(owner)) result.set(owner, []);
      result.get(owner).push(biz);
    });
  }));

  return result;
}